import { useParams, Link } from "react-router-dom";
import { getGuideBySlug } from "../content/marketplace-content-loader";
import type { Preview } from "../content/marketplace-content-loader";
import { SIGNUP_FORM_URL } from "../external-links";

/* ────── Utility Icons ────── */

function DownloadIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 2v8M5 7.5L8 10.5l3-3" />
      <path d="M3 13h10" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg viewBox="0 0 12 12" fill="#C4993C" stroke="none">
      <path d="M6 .8l1.5 3 3.3.5-2.4 2.3.6 3.4L6 8.2 3 10l.6-3.4L1.2 4.3l3.3-.5z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="#7B9E87" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 8.5l3.5 3.5L13 5" />
    </svg>
  );
}

/* ────── Preview Mockup Cards ────── */

function PreviewMetric({ preview }: { preview: Preview }) {
  return (
    <div className="skd-mock-card">
      <div className="skd-mock-header">
        <span className="skd-mock-brand">Kin</span>
      </div>
      <div className="skd-mock-title">{preview.title}</div>
      <div className="skd-mock-metric">
        <span className="skd-mock-value">{preview.value}</span>
        <span className="skd-mock-unit">{preview.unit}</span>
      </div>
      <svg className="skd-mock-chart-line" viewBox="0 0 160 40" fill="none">
        <path d="M0 35 Q20 30, 40 28 T80 18 T120 12 T160 8" stroke="var(--accent-sage)" strokeWidth="2" strokeLinecap="round" opacity="0.6" />
        <path d="M0 35 Q20 30, 40 28 T80 18 T120 12 T160 8 V40 H0 Z" fill="var(--accent-sage-soft)" />
      </svg>
    </div>
  );
}

function PreviewChecklist({ preview }: { preview: Preview }) {
  return (
    <div className="skd-mock-card skd-mock-card--small">
      <div className="skd-mock-header">
        <span className="skd-mock-brand">Kin</span>
      </div>
      <div className="skd-mock-title">{preview.title}</div>
      <div className="skd-mock-checklist">
        <div className="skd-mock-check-row">
          <div className="skd-mock-checkbox skd-mock-checkbox--done" />
          <div className="skd-mock-check-line" style={{ width: "80%" }} />
        </div>
        <div className="skd-mock-check-row">
          <div className="skd-mock-checkbox skd-mock-checkbox--done" />
          <div className="skd-mock-check-line" style={{ width: "65%" }} />
        </div>
        <div className="skd-mock-check-row">
          <div className="skd-mock-checkbox" />
          <div className="skd-mock-check-line" style={{ width: "72%" }} />
        </div>
      </div>
    </div>
  );
}

function PreviewChart({ preview }: { preview: Preview }) {
  return (
    <div className="skd-mock-card skd-mock-card--tall">
      <div className="skd-mock-header">
        <span className="skd-mock-brand">Kin</span>
        <span className="skd-mock-subtitle-tag">Biomarker Analysis</span>
      </div>
      <div className="skd-mock-title">{preview.subtitle}</div>
      <div className="skd-mock-metric skd-mock-metric--sm">
        <span className="skd-mock-value">{preview.value}</span>
        <span className="skd-mock-unit">{preview.unit}</span>
      </div>
      <svg className="skd-mock-bar-chart" viewBox="0 0 140 50" fill="none">
        <rect x="5" y="30" width="14" height="20" rx="3" fill="var(--accent-sage)" opacity="0.3" />
        <rect x="25" y="20" width="14" height="30" rx="3" fill="var(--accent-sage)" opacity="0.4" />
        <rect x="45" y="10" width="14" height="40" rx="3" fill="var(--accent-sage)" opacity="0.5" />
        <rect x="65" y="15" width="14" height="35" rx="3" fill="var(--accent-sage)" opacity="0.6" />
        <rect x="85" y="25" width="14" height="25" rx="3" fill="var(--accent-sage)" opacity="0.45" />
        <rect x="105" y="18" width="14" height="32" rx="3" fill="var(--accent-sage)" opacity="0.55" />
        <rect x="125" y="22" width="14" height="28" rx="3" fill="var(--accent-sage)" opacity="0.35" />
      </svg>
    </div>
  );
}

function PreviewCard({ preview }: { preview: Preview }) {
  switch (preview.type) {
    case "metric":
      return <PreviewMetric preview={preview} />;
    case "checklist":
      return <PreviewChecklist preview={preview} />;
    case "chart":
      return <PreviewChart preview={preview} />;
  }
}

/* ────── Page Component ────── */

export function GuideDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const guide = slug ? getGuideBySlug(slug) : undefined;

  if (!guide) {
    return (
      <div className="skd-page">
        <div className="skd-not-found">
          <h1>Guide not found</h1>
          <p>The guide you're looking for doesn't exist.</p>
          <Link to="/marketplace" className="skd-back-link">
            Back to Marketplace
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="skd-page">
      {/* Hero */}
      <div className="skd-hero">
        <div className="skd-hero-text">
          <h1 className="skd-hero-title">{guide.name}.</h1>
          <p className="skd-hero-subtitle">{guide.subtitle}</p>
        </div>
        <div className="skd-hero-actions">
          <a href={SIGNUP_FORM_URL} target="_blank" rel="noopener noreferrer" className="skd-install-btn" style={{ background: guide.buttonBg }}>
            Install Guide
          </a>
          <div className="skd-installs">
            <DownloadIcon />
            <div className="skd-installs-text">
              <span className="skd-installs-value">{guide.installs}</span>
              <span className="skd-installs-label">Installs</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bento Grid */}
      <div className="skd-bento">
        {/* About */}
        <div className="skd-card skd-about">
          <h2 className="skd-card-title">About this Guide</h2>
          {guide.about.map((p, i) => (
            <p key={i} className="skd-about-text">{p}</p>
          ))}
          <h3 className="skd-cap-heading">What You Can Do</h3>
          <div className="skd-cap-list">
            {guide.capabilities.map((cap) => (
              <div key={cap.title} className="skd-cap-item">
                <div className="skd-cap-icon" style={{ background: cap.color }}>
                  {cap.icon}
                </div>
                <div className="skd-cap-content">
                  <span className="skd-cap-title">{cap.title}:</span>
                  <span className="skd-cap-desc">{cap.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Preview */}
        <div className="skd-card skd-preview">
          <h2 className="skd-card-title">Guide Preview</h2>
          <div className="skd-preview-grid">
            {guide.previews.map((preview) => (
              <PreviewCard key={preview.title} preview={preview} />
            ))}
          </div>
        </div>

        {/* Creator */}
        <div className="skd-card skd-creator">
          <span className="skd-creator-label">Created By</span>
          {guide.creator.verified && (
            <span className="skd-verified-badge">
              <CheckIcon /> Verified Creator
            </span>
          )}
          <h3 className="skd-creator-name">{guide.creator.name}</h3>
          <p className="skd-creator-desc">{guide.creator.desc}</p>
        </div>

        {/* Reviews */}
        <div className="skd-card skd-reviews">
          <h2 className="skd-card-title">Ratings &amp; Reviews</h2>
          <div className="skd-reviews-layout">
            <div className="skd-rating-big">
              <span className="skd-rating-number">{guide.ratingNum}</span>
              <span className="skd-rating-slash">/5</span>
              <div className="skd-rating-stars">
                {Array.from({ length: 5 }).map((_, i) => (
                  <StarIcon key={i} />
                ))}
              </div>
            </div>
            {guide.reviews.map((review) => (
              <div key={review.quote} className="skd-review-card">
                <p className="skd-review-quote">{review.quote}</p>
                <p className="skd-review-detail">{review.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}
